// ===============================
// usePricingData Hook
// Fetches pricing data from Admin Mothership API
// Single source of truth: https://api.axoix.in
// ===============================

import { useTranslation } from 'react-i18next';
import { useState, useEffect, useMemo } from 'react';
import { fetchPricingData, PricingTierAPI, PricingAddonAPI, CompletePricingData } from '../services/api/pricingService';

export interface PricingFeature {
  text: string;
  included: boolean;
  highlight?: boolean;
}

export interface PricingTier {
  id: string;
  name: string;
  shipName: string;
  description: string;
  monthlyPrice: number | null;
  quarterlyPrice: number | null;
  sixMonthPrice: number | null;
  yearlyPrice: number | null;
  fiveYearPrice: number | null;
  tenYearPrice: number | null;
  originalMonthlyPrice: number | null;
  originalQuarterlyPrice: number | null;
  originalSixMonthPrice: number | null;
  originalYearlyPrice: number | null;
  originalFiveYearPrice: number | null;
  originalTenYearPrice: number | null;
  discountPercent: number;
  currency: string;
  popular?: boolean;
  features: PricingFeature[];
  ctaText: string;
  ctaLink: string;
  badge?: string;
  comingSoon?: boolean;
  /** Raw `pricing_tiers.feature_limits` — allowances plus overage rates. */
  featureLimits?: Record<string, unknown>;
}

export interface SpecialtyAddon {
  id: string;
  name: string;
  description: string;
  monthlyPrice: number;
  yearlyPrice: number;
  currency: string;
  industries: string[];
  features: string[];
  character: string;
  icon: string;
  badge?: string;
  comingSoon?: boolean;
}

export interface FeatureCategory {
  name: string;
  features: {
    name: string;
    explorer: string | boolean;
    officer: string | boolean;
    commander: string | boolean;
    titan: string | boolean ;
  }[];
}

export interface FAQItem {
  question: string;
  answer: string;
}

// Transform API tier to local format
const transformTier = (apiTier: PricingTierAPI, t: (key: string) => string): PricingTier => {
  // Feature bullets come from i18n (localized + kept canonical), NOT the API:
  // the admin `features` JSON is English-only and drifts (e.g. stale voice
  // counts). API still drives prices, badges, limits, comingSoon.
  const features: PricingFeature[] = getDefaultFeatures(apiTier.id, t);

  return {
    id: apiTier.id,
    name: apiTier.name,
    shipName: apiTier.shipName,
    description: apiTier.description || t(`pricingData.tiers.${apiTier.id}.description`),
    monthlyPrice: apiTier.monthlyPrice,
    quarterlyPrice: apiTier.quarterlyPrice,
    sixMonthPrice: apiTier.sixMonthPrice,
    yearlyPrice: apiTier.yearlyPrice,
    fiveYearPrice: apiTier.fiveYearPrice,
    tenYearPrice: apiTier.tenYearPrice,
    originalMonthlyPrice: apiTier.originalMonthlyPrice,
    originalQuarterlyPrice: apiTier.originalQuarterlyPrice,
    originalSixMonthPrice: apiTier.originalSixMonthPrice,
    originalYearlyPrice: apiTier.originalYearlyPrice,
    originalFiveYearPrice: apiTier.originalFiveYearPrice,
    originalTenYearPrice: apiTier.originalTenYearPrice,
    discountPercent: apiTier.discountPercent,
    currency: apiTier.currency,
    popular: apiTier.isPopular,
    features,
    ctaText: apiTier.ctaText || t(`pricingData.tiers.${apiTier.id}.ctaText`),
    ctaLink: apiTier.ctaLink,
    badge: apiTier.badge || undefined,
    comingSoon: apiTier.isComingSoon,
    // Passed through so callers can render the actual allowances. The PG
    // pricing table needs beds/rooms/locations/employees and their per-unit
    // overage rates; without this it silently rendered an empty object.
    featureLimits: apiTier.featureLimits || {},
  };
};

// Transform API addon to local format
const transformAddon = (apiAddon: PricingAddonAPI): SpecialtyAddon => {
  return {
    id: apiAddon.id,
    name: apiAddon.name,
    description: apiAddon.description || '',
    monthlyPrice: apiAddon.monthlyPrice || 0,
    yearlyPrice: apiAddon.yearlyPrice || 0,
    currency: apiAddon.currency,
    industries: apiAddon.targetIndustries || [],
    features: apiAddon.features || [],
    character: apiAddon.character || '',
    icon: apiAddon.icon || 'fas fa-cube',
    badge: apiAddon.badge || undefined,
    comingSoon: apiAddon.status === 'coming_soon',
  };
};

// Default features for tiers (fallback if API doesn't provide)
const getDefaultFeatures = (tierId: string, t: (key: string) => string): PricingFeature[] => {
  const featureKeys: Record<string, string[]> = {
    explorer: ['basicDashboard', 'axoixLimited', 'sealEsign', 'remedyComplaints', 'singleLanguage', 'communitySupport', 'standardAnalytics', 'fullAiAccess', 'voiceCommands', 'multiLanguage'],
    officer: ['everythingExplorer', 'employees', 'verticals', 'fullAiCharacters', 'voiceControl', 'multiLanguage', 'emailSuite', 'storage', 'storefrontThemes', 'emailSupport'],
    commander: ['everythingOfficer', 'employees', 'verticals', 'storefrontThemes', 'storage', 'emailAccounts', 'aiCosting', 'prioritySupport', 'advancedAnalytics', 'apiAccess'],
    titan: ['everythingCommander', 'employees', 'verticals', 'allAddons', 'storage', 'emailAccounts', 'aiCosting', 'unlimitedApi', 'whiteLabel', 'customTraining', 'dedicatedManager', 'phoneSupport'],
  };

  const includedMap: Record<string, boolean[]> = {
    explorer: [true, true, true, true, true, true, true, false, false, false],
    officer: [true, true, true, true, true, true, true, true, true, true],
    commander: [true, true, true, true, true, true, true, true, true, true],
    titan: [true, true, true, true, true, true, true, true, true, true, true, true],
  };

  const highlightMap: Record<string, number[]> = {
    explorer: [],
    officer: [0],
    commander: [0],
    titan: [0, 3],
  };

  const keys = featureKeys[tierId] || [];
  const included = includedMap[tierId] || [];
  const highlights = highlightMap[tierId] || [];

  return keys.map((key, index) => ({
    text: t(`pricingData.tiers.${tierId}.features.${key}`),
    included: included[index] ?? true,
    highlight: highlights.includes(index),
  }));
};

export const usePricingData = () => {
  const { t } = useTranslation();
  const [apiData, setApiData] = useState<CompletePricingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Fetch pricing data from Admin API
  useEffect(() => {
    let mounted = true;

    const loadPricing = async () => {
      try {
        setLoading(true);
        const data = await fetchPricingData();
        if (mounted) {
          if (data && data.tiers && data.tiers.length > 0) {
            setApiData(data);
            setError(null);
          } else {
            setApiData(null);
          }
        }
      } catch (err) {
        console.warn('[usePricingData] Failed to fetch from Mothership API, using hardcoded fallback:', err);
        if (mounted) {
          setError(err as Error);
          setApiData(null);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadPricing();

    return () => {
      mounted = false;
    };
  }, []);

  // Transform API tiers to local format
  const pricingTiers = useMemo((): PricingTier[] => {
    let tiers: PricingTier[];
    if (apiData?.tiers && apiData.tiers.length > 0) {
      tiers = apiData.tiers
        .filter(tier => tier.isActive)
        .sort((a, b) => a.tierLevel - b.tierLevel)
        .map(tier => transformTier(tier, t));
    } else {
      // Fallback to hardcoded only if Mothership API fails or returns no data
      tiers = getFallbackTiers(t);
    }

    // Defensive coercions — admin DB sometimes returns null prices / missing
    // tier-specific ctaLinks. Without these, Explorer renders as "Contact Us"
    // (null price) and paid CTAs route to /signup without the ?tier= param.
    return tiers.map(tier => {
      const out = { ...tier };
      if (tier.id === 'explorer' && (out.monthlyPrice == null)) {
        out.monthlyPrice = 0;
        out.yearlyPrice = 0;
        out.quarterlyPrice = 0;
        out.sixMonthPrice = 0;
      }
      if (tier.id === 'titan' && (!out.ctaLink || out.ctaLink.includes('/signup'))) {
        out.ctaLink = '/contact?tier=titan';
        out.ctaText = t('pricingData.tiers.titan.ctaText') || 'Contact Sales';
      } else if (tier.id !== 'explorer' && out.ctaLink && !out.ctaLink.includes('tier=')) {
        out.ctaLink = `/signup?tier=${tier.id}`;
      }
      return out;
    });
  }, [apiData, t]);

  // Transform API addons to local format
  const specialtyAddons = useMemo((): SpecialtyAddon[] => {
    if (apiData?.addons && apiData.addons.length > 0) {
      return apiData.addons
        .filter(addon => addon.isActive)
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map(transformAddon);
    }

    // Fallback to hardcoded if API fails
    return getFallbackAddons(t);
  }, [apiData, t]);

  // Feature comparison (use API data or fallback)
  const featureComparison = useMemo((): FeatureCategory[] => {
    // For now, use translations as feature comparison is more complex
    return getFeatureComparison(t);
  }, [t]);

  const comparisonHeaders = useMemo(() => ({
    feature: t('pricingData.comparison.tableHeaders.feature'),
    explorer: t('pricingData.comparison.tableHeaders.explorer') || 'Explorer',
    officer: t('pricingData.comparison.tableHeaders.officer'),
    commander: t('pricingData.comparison.tableHeaders.commander'),
    titan: t('pricingData.comparison.tableHeaders.titan') || 'Titan',
  }), [t]);

  return {
    pricingTiers,
    specialtyAddons,
    featureComparison,
    comparisonHeaders,
    loading,
    error,
    isFromAPI: apiData !== null,
  };
};

// Fallback tiers if API fails
const getFallbackTiers = (t: (key: string) => string): PricingTier[] => [
  {
    id: 'explorer',
    name: t('pricingData.tiers.explorer.name'),
    shipName: t('pricingData.tiers.explorer.shipName'),
    description: t('pricingData.tiers.explorer.description'),
    monthlyPrice: 0,
    quarterlyPrice: 0,
    sixMonthPrice: 0,
    yearlyPrice: 0,
    fiveYearPrice: null,
    tenYearPrice: null,
    originalMonthlyPrice: 0,
    originalQuarterlyPrice: 0,
    originalSixMonthPrice: 0,
    originalYearlyPrice: 0,
    originalFiveYearPrice: null,
    originalTenYearPrice: null,
    discountPercent: 0,
    currency: 'INR',
    features: getDefaultFeatures('explorer', t),
    ctaText: t('pricingData.tiers.explorer.ctaText'),
    ctaLink: '/signup',
    badge: t('pricingData.tiers.explorer.badge'),
    featureLimits: {
      beds: 20,
      bed_overage_monthly: 75,
      rooms: 15,
      room_overage_monthly: 50,
      locations: 1,
      location_overage_monthly: 500,
      employees: 5,
    },
  },
  {
    id: 'officer',
    name: t('pricingData.tiers.officer.name'),
    shipName: t('pricingData.tiers.officer.shipName'),
    description: t('pricingData.tiers.officer.description'),
    monthlyPrice: 1500,
    quarterlyPrice: 4275,
    sixMonthPrice: 8100,
    yearlyPrice: 14400,
    fiveYearPrice: null,
    tenYearPrice: null,
    originalMonthlyPrice: null,
    originalQuarterlyPrice: null,
    originalSixMonthPrice: null,
    originalYearlyPrice: null,
    originalFiveYearPrice: null,
    originalTenYearPrice: null,
    discountPercent: 0,
    currency: 'INR',
    popular: true,
    features: getDefaultFeatures('officer', t),
    ctaText: t('pricingData.tiers.officer.ctaText'),
    ctaLink: '/signup?tier=officer',
    badge: t('pricingData.tiers.officer.badge'),
    featureLimits: {
      beds: 100,
      bed_overage_monthly: 22.5,
      rooms: 60,
      room_overage_monthly: 40,
      locations: 3,
      location_overage_monthly: 400,
      employees: 20,
    },
  },
  {
    id: 'commander',
    name: t('pricingData.tiers.commander.name'),
    shipName: t('pricingData.tiers.commander.shipName'),
    description: t('pricingData.tiers.commander.description'),
    monthlyPrice: 3600,
    quarterlyPrice: 10260,
    sixMonthPrice: 19440,
    yearlyPrice: 34560,
    fiveYearPrice: null,
    tenYearPrice: null,
    originalMonthlyPrice: null,
    originalQuarterlyPrice: null,
    originalSixMonthPrice: null,
    originalYearlyPrice: null,
    originalFiveYearPrice: null,
    originalTenYearPrice: null,
    discountPercent: 0,
    currency: 'INR',
    features: getDefaultFeatures('commander', t),
    ctaText: t('pricingData.tiers.commander.ctaText'),
    ctaLink: '/signup?tier=commander',
    badge: t('pricingData.tiers.commander.badge'),
    featureLimits: {
      beds: 300,
      bed_overage_monthly: 18,
      rooms: 180,
      room_overage_monthly: 30,
      locations: 15,
      location_overage_monthly: 300,
      employees: 50,
    },
  },
  {
    id: 'titan',
    name: t('pricingData.tiers.titan.name'),
    shipName: t('pricingData.tiers.titan.shipName'),
    description: t('pricingData.tiers.titan.description'),
    monthlyPrice: 7500,
    quarterlyPrice: 21375,
    sixMonthPrice: 40500,
    yearlyPrice: 72000,
    fiveYearPrice: null,
    tenYearPrice: null,
    originalMonthlyPrice: null,
    originalQuarterlyPrice: null,
    originalSixMonthPrice: null,
    originalYearlyPrice: null,
    originalFiveYearPrice: null,
    originalTenYearPrice: null,
    discountPercent: 0,
    currency: 'INR',
    features: getDefaultFeatures('titan', t),
    ctaText: t('pricingData.tiers.titan.ctaText'),
    ctaLink: '/contact?tier=titan',
    badge: t('pricingData.tiers.titan.badge'),
    featureLimits: {
      beds: 750,
      bed_overage_monthly: 15,
      rooms: 450,
      room_overage_monthly: 25,
      locations: 35,
      location_overage_monthly: 250,
      employees: 100,
    },
  },
];

// Fallback addons if API fails
const getFallbackAddons = (t: (key: string) => string): SpecialtyAddon[] => [
  {
    id: 'tender_management',
    name: t('pricingData.addons.tenderManagement.name'),
    description: t('pricingData.addons.tenderManagement.description'),
    monthlyPrice: 1500,
    yearlyPrice: 12999,
    currency: 'INR',
    industries: ['gov_contractor', 'construction'],
    features: [
      t('pricingData.addons.tenderManagement.features.nitExtractor'),
      t('pricingData.addons.tenderManagement.features.cpwdRates'),
      t('pricingData.addons.tenderManagement.features.boqQuotes'),
      t('pricingData.addons.tenderManagement.features.autoProject'),
      t('pricingData.addons.tenderManagement.features.raBills'),
      t('pricingData.addons.tenderManagement.features.emdTracking'),
      t('pricingData.addons.tenderManagement.features.deadlineAlerts'),
    ],
    character: 'PROBE',
    icon: 'fas fa-file-contract',
    badge: t('pricingData.addons.tenderManagement.badge'),
    comingSoon: true,
  },
  {
    id: 'link_phone_system',
    name: t('pricingData.addons.linkPhone.name'),
    description: t('pricingData.addons.linkPhone.description'),
    monthlyPrice: 2500,
    yearlyPrice: 24999,
    currency: 'INR',
    industries: ['all'],
    features: [
      t('pricingData.addons.linkPhone.features.freePbx'),
      t('pricingData.addons.linkPhone.features.freeExtensions'),
      t('pricingData.addons.linkPhone.features.freeVoicemail'),
      t('pricingData.addons.linkPhone.features.didNumber'),
      t('pricingData.addons.linkPhone.features.ivrBuilder'),
      t('pricingData.addons.linkPhone.features.callRecording'),
      t('pricingData.addons.linkPhone.features.clickToCall'),
      t('pricingData.addons.linkPhone.features.missedAlerts'),
    ],
    character: 'LINK',
    icon: 'fas fa-phone-volume',
    badge: t('pricingData.addons.linkPhone.badge'),
    comingSoon: true,
  },
];

// Feature comparison (uses translations)
const getFeatureComparison = (t: (key: string) => string): FeatureCategory[] => [
  {
    name: t('pricingData.comparison.categories.aiCapabilities'),
    features: [
      { name: t('pricingData.comparison.features.axoixAssistant'), explorer: t('pricingData.comparison.values.limited'), officer: t('pricingData.comparison.values.full'), commander: t('pricingData.comparison.values.fullCustom'), titan: t('pricingData.comparison.values.fullCustom') },
      { name: t('pricingData.comparison.features.aiCharacters'), explorer: '1', officer: '22', commander: '22', titan: '22 + Custom' },
      { name: t('pricingData.comparison.features.voiceCommands'), explorer: false, officer: '81 languages', commander: '81 languages', titan: '81 + Custom' },
      { name: t('pricingData.comparison.features.languageSupport'), explorer: '1', officer: '81', commander: '81', titan: '81' },
      { name: t('pricingData.comparison.features.aiTraining'), explorer: false, officer: false, commander: false, titan: true },
    ],
  },
  {
    name: t('pricingData.comparison.categories.dashboardUi'),
    features: [
      { name: t('pricingData.comparison.features.dashboardAccess'), explorer: t('pricingData.comparison.values.basic'), officer: t('pricingData.comparison.values.full'), commander: t('pricingData.comparison.values.full'), titan: t('pricingData.comparison.values.full') },
      { name: t('pricingData.comparison.features.premiumThemes'), explorer: false, officer: '3/vertical', commander: 'All', titan: 'All + Custom' },
      { name: t('pricingData.comparison.features.avatar3d'), explorer: false, officer: t('pricingData.comparison.values.with3dWeb'), commander: t('pricingData.comparison.values.with3dWeb'), titan: t('pricingData.comparison.values.with3dWeb') },
      { name: t('pricingData.comparison.features.analytics'), explorer: t('pricingData.comparison.values.basic'), officer: t('pricingData.comparison.values.advanced'), commander: t('pricingData.comparison.values.advanced'), titan: t('pricingData.comparison.values.enterprise') },
      { name: t('pricingData.comparison.features.customBranding'), explorer: false, officer: false, commander: false, titan: true },
    ],
  },
  {
    name: t('pricingData.comparison.categories.supportAccess'),
    features: [
      { name: t('pricingData.comparison.features.communitySupport'), explorer: true, officer: true, commander: true, titan: true },
      { name: t('pricingData.comparison.features.emailSupport'), explorer: false, officer: true, commander: t('pricingData.comparison.values.priority'), titan: t('pricingData.comparison.values.priority') },
      { name: t('pricingData.comparison.features.phoneSupport'), explorer: false, officer: false, commander: true, titan: '24/7' },
      { name: t('pricingData.comparison.features.dedicatedManager'), explorer: false, officer: false, commander: false, titan: true },
      { name: t('pricingData.comparison.features.sla'), explorer: false, officer: false, commander: false, titan: '99.9%' },
    ],
  },
  {
    name: t('pricingData.comparison.categories.apiIntegrationsAccess'),
    features: [
      { name: t('pricingData.comparison.features.apiAccess'), explorer: false, officer: '5,000/mo', commander: '5,000/mo', titan: t('pricingData.comparison.values.unlimited') },
      { name: t('pricingData.comparison.features.webhooks'), explorer: false, officer: true, commander: true, titan: true },
      { name: t('pricingData.comparison.features.customIntegrations'), explorer: false, officer: true, commander: true, titan: true },
      { name: t('pricingData.comparison.features.onPremise'), explorer: false, officer: false, commander: false, titan: true },
    ],
  },
  {
    name: t('pricingData.comparison.categories.teamManagement'),
    features: [
      { name: t('pricingData.comparison.features.teamMembers'), explorer: '5', officer: '20', commander: '50', titan: '100' },
      { name: t('pricingData.comparison.features.roleManagement'), explorer: false, officer: t('pricingData.comparison.values.basic'), commander: t('pricingData.comparison.values.advanced'), titan: t('pricingData.comparison.values.enterprise') },
      { name: t('pricingData.comparison.features.auditLogs'), explorer: false, officer: true, commander: true, titan: true },
    ],
  },
];

// Hook for service cards
export const useServiceCards = () => {
  const { t } = useTranslation();

  return useMemo(() => [
    { id: 1, title: t('serviceCards.card1.title'), desc: t('serviceCards.card1.desc'), link: '/team' },
    { id: 2, title: t('serviceCards.card2.title'), desc: t('serviceCards.card2.desc'), link: '/pricing' },
    { id: 3, title: t('serviceCards.card3.title'), desc: t('serviceCards.card3.desc'), link: '/service' },
    { id: 4, title: t('serviceCards.card4.title'), desc: t('serviceCards.card4.desc'), link: '/service' },
    { id: 5, title: t('serviceCards.card5.title'), desc: t('serviceCards.card5.desc'), link: '/service' },
    { id: 6, title: t('serviceCards.card6.title'), desc: t('serviceCards.card6.desc'), link: '/pricing' },
  ], [t]);
};

// Hook for industries data
export const useIndustriesData = () => {
  const { t } = useTranslation();

  return useMemo(() => ({
    subtitle: t('industries.subtitle'),
    title: t('industries.title'),
    description: t('industries.description'),
    industries: [
      { id: 'education', title: t('industriesData.education.title'), desc: t('industriesData.education.desc') },
      { id: 'logistics', title: t('industriesData.logistics.title'), desc: t('industriesData.logistics.desc') },
      { id: 'marketing', title: t('industriesData.marketing.title'), desc: t('industriesData.marketing.desc') },
      { id: 'healthcare', title: t('industriesData.healthcare.title'), desc: t('industriesData.healthcare.desc') },
      { id: 'finance', title: t('industriesData.finance.title'), desc: t('industriesData.finance.desc') },
      { id: 'manufacturing', title: t('industriesData.manufacturing.title'), desc: t('industriesData.manufacturing.desc') },
      { id: 'retail', title: t('industriesData.retail.title'), desc: t('industriesData.retail.desc') },
      { id: 'construction', title: t('industriesData.construction.title'), desc: t('industriesData.construction.desc') },
      { id: 'professional', title: t('industriesData.professional.title'), desc: t('industriesData.professional.desc') },
    ],
  }), [t]);
};

// Hook for FAQ data
export const useFAQData = () => {
  const { t } = useTranslation();

  return useMemo(() => ({
    categories: {
      all: t('faq.categories.all'),
      general: t('faq.categories.general'),
      pricing: t('faq.categories.pricing'),
      technical: t('faq.categories.technical'),
      link: t('faq.categories.link'),
    },
    general: [
      { question: t('faqData.general.q1.question'), answer: t('faqData.general.q1.answer') },
      { question: t('faqData.general.q2.question'), answer: t('faqData.general.q2.answer') },
      { question: t('faqData.general.q3.question'), answer: t('faqData.general.q3.answer') },
      { question: t('faqData.general.q4.question'), answer: t('faqData.general.q4.answer') },
      { question: t('faqData.general.q5.question'), answer: t('faqData.general.q5.answer') },
    ],
    link: [
      { question: t('faqData.link.q1.question'), answer: t('faqData.link.q1.answer') },
      { question: t('faqData.link.q2.question'), answer: t('faqData.link.q2.answer') },
      { question: t('faqData.link.q3.question'), answer: t('faqData.link.q3.answer') },
      { question: t('faqData.link.q4.question'), answer: t('faqData.link.q4.answer') },
      { question: t('faqData.link.q5.question'), answer: t('faqData.link.q5.answer') },
      { question: t('faqData.link.q6.question'), answer: t('faqData.link.q6.answer') },
    ],
    technical: [
      { question: t('faqData.technical.q1.question'), answer: t('faqData.technical.q1.answer') },
      { question: t('faqData.technical.q2.question'), answer: t('faqData.technical.q2.answer') },
      { question: t('faqData.technical.q3.question'), answer: t('faqData.technical.q3.answer') },
      { question: t('faqData.technical.q4.question'), answer: t('faqData.technical.q4.answer') },
      { question: t('faqData.technical.q5.question'), answer: t('faqData.technical.q5.answer') },
      { question: t('faqData.technical.q6.question'), answer: t('faqData.technical.q6.answer') },
    ],
    pricing: [
      { question: t('faqData.pricing.q1.question'), answer: t('faqData.pricing.q1.answer') },
      { question: t('faqData.pricing.q2.question'), answer: t('faqData.pricing.q2.answer') },
      { question: t('faqData.pricing.q3.question'), answer: t('faqData.pricing.q3.answer') },
      { question: t('faqData.pricing.q4.question'), answer: t('faqData.pricing.q4.answer') },
      { question: t('faqData.pricing.q5.question'), answer: t('faqData.pricing.q5.answer') },
      { question: t('faqData.pricing.q6.question'), answer: t('faqData.pricing.q6.answer') },
    ],
  }), [t]);
};

export default usePricingData;
